import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { isPresent, useTimeZoneContext } from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';
import { getReplayOverlay, useSession } from '@ais-ui/websockets';
import { useToast } from '@ais-ui/ui-core';
import {
  getDimensionsFromMap,
  useMapState,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { useDebounce } from '@ais-ui/custom-hooks';
import {
  getDateInUtcWithAddedSeconds,
  useReplayContext,
} from './ReplayContext';
import { useViewMode, ViewMode } from '../../components/ViewModeProvider';

const getTimestampWithAddedSeconds = (
  date: Date | undefined,
  secondsToAdd: number
): string => {
  const newDate = getDateInUtcWithAddedSeconds(date, secondsToAdd);

  if (!newDate) return '';

  return newDate.toISOString().replace(/\.\d{3}/, '');
};

const useGetReplayOverlay = () => {
  const { messages } = useLocalization();
  const { replaySession } = useSession();
  const { toast } = useToast();
  const { timeZone } = useTimeZoneContext();

  const { map, mapProjection, userProjection } = useOpenLayersContext();

  // Function to calculate dimensions and return getReplayOverlayProps
  const getOverlayProps = (timestamp: string, replayIdentifier: string) => {
    const dimensions = getDimensionsFromMap(map, mapProjection, userProjection);

    if (dimensions) {
      const { center, zoomLevel, size, northEast, southWest } = dimensions;
      return {
        centerLat: center.lat,
        centerLon: center.lng,
        zoomLevel: Math.round(zoomLevel),
        width: size.width,
        height: size.height,
        leftLon: southWest.lng,
        rightLon: northEast.lng,
        timestamp,
        replayIdentifier: replayIdentifier,
      };
    } else {
      return null;
    }
  };

  return async (timestamp: string, replayIdentifier: string) => {
    const getReplayOverlayProps = getOverlayProps(timestamp, replayIdentifier);
    if (!getReplayOverlayProps) {
      return null;
    }

    // Await the result of getReplayOverlay to ensure the operation completes before returning
    try {
      if (!replaySession) {
        toast({
          title: messages.genericToastTitleError,
          description: messages.replayErrorMessageNoReplaySession,
          variant: 'destructive',
        });
        return null;
      }

      const result = await getReplayOverlay(
        replaySession,
        getReplayOverlayProps,
        timeZone
      );
      return result;
    } catch (error) {
      toast({
        title: messages.genericToastTitleError,
        description: messages.replayErrorMessageFailedToGetOverlay,
        variant: 'destructive',
      });
      console.error('Failed to get replay overlay:', error);
      return null;
    }
  };
};

export const usePlayReplay = () => {
  const {
    startDate,
    replaySpeed,
    isPlaying,
    replayIdentifier,
    setSecondsSinceStart,
    setReplayIdentifier,
    secondsSinceStart,
    maxTimeInSeconds,
    hasLoaded,
    setHasLoaded,
    setReplayRequestDatetime,
    minZoom,
    tracker,
  } = useReplayContext();

  const fetchReplayOverlay = useGetReplayOverlay();
  const { map } = useOpenLayersContext();
  const { currentZoom } = useMapState();
  const [mapMoved, setMapMoved] = useState(false);
  const debouncedMapMoved = useDebounce(mapMoved, 500);
  const { toast } = useToast();
  const { messages } = useLocalization();

  const fetchOverlay = useCallback(async () => {
    if (!isPlaying) return;
    if (currentZoom < minZoom) {
      toast({
        title: messages.genericToastTitleWarning,
        description: messages.replayErrorMessageZoomLevelTooLow,
        variant: 'warning',
      });
    }

    const transactionName = hasLoaded
      ? 'replay.overlay.subsequentLoad'
      : 'replay.overlay.firstLoad';

    tracker.startTransaction(transactionName, 'workflow', {
      zoomLevel: currentZoom,
      startDate,
      secondsSinceStart,
    });

    const timestamp = getTimestampWithAddedSeconds(
      startDate,
      secondsSinceStart
    );
    try {
      const newReplayIdentifier = uuidv4();
      fetchReplayOverlay(timestamp, newReplayIdentifier);
      setReplayRequestDatetime(new Date());
      setReplayIdentifier(newReplayIdentifier);
    } catch (error) {
      console.error(error);
    }
  }, [
    isPlaying,
    startDate,
    secondsSinceStart,
    fetchReplayOverlay,
    setReplayRequestDatetime,
    setReplayIdentifier,
  ]);

  useEffect(() => {
    const handleMapMove = () => {
      if (tracker.isTracking) {
        tracker.cancelTransaction('Map moved during replay');
      }
      setReplayIdentifier(null);
      setHasLoaded(false);
      setMapMoved(true);
    };
    map.on('moveend', handleMapMove);
    return () => {
      map.un('moveend', handleMapMove);
    };
  }, [map, setHasLoaded, setReplayIdentifier, tracker]);

  useEffect(() => {
    // Do not fetch overlay if we are not playing
    if (!isPlaying) {
      return;
    }
    // Do not fetch new overlay if the map has moved or is currently being debounced
    if (mapMoved || debouncedMapMoved) {
      if (mapMoved && debouncedMapMoved) {
        setMapMoved(false);
      }
      return;
    }

    // When replayIdentifier is present, it means we are waiting for the replay overlay to be fetched
    if (isPresent(replayIdentifier)) {
      return;
    }
    fetchOverlay();
    setSecondsSinceStart((currentValue) => {
      const nextValue = currentValue + replaySpeed;

      // Ensure the secondsSinceStart does not exceed the maximum value
      return nextValue > maxTimeInSeconds ? maxTimeInSeconds : nextValue;
    });
  }, [
    replayIdentifier,
    startDate,
    secondsSinceStart,
    maxTimeInSeconds,
    replaySpeed,
    isPlaying,
    fetchReplayOverlay,
    setReplayIdentifier,
    mapMoved,
    debouncedMapMoved,
    fetchOverlay,
    setSecondsSinceStart,
  ]);
};

export const useStartStopPauseReplay = () => {
  const { setIsPlaying, resetReplay, tracker, replayIdentifier } =
    useReplayContext();
  const { setViewMode } = useViewMode();
  const { toast } = useToast();
  const { messages } = useLocalization();

  const startReplay = useCallback(() => {
    setIsPlaying(true);
    setViewMode(ViewMode.Replay);
    toast({
      title: messages.genericToastTitleSuccess,
      description: messages.replayToastStartedMessage,
    });
  }, [toast, messages, setIsPlaying, setViewMode]);

  const stopReplay = useCallback(() => {
    if (tracker.isTracking) {
      tracker.cancelTransaction('Replay stopped by user');
    }

    resetReplay({ resetDate: true });
    setViewMode(ViewMode.Normal);
    toast({
      title: messages.genericToastTitleSuccess,
      description: messages.replayToastStoppedMessage,
    });
  }, [resetReplay, setViewMode, toast, messages, tracker]);

  const pauseReplay = useCallback(() => {
    if (tracker.isTracking) {
      tracker.cancelTransaction('Replay paused by user');
    }

    setIsPlaying(false);
    toast({
      title: messages.genericToastTitleSuccess,
      description: messages.replayToastPausedMessage,
    });
  }, [toast, messages, setIsPlaying, tracker]);
  return { startReplay, stopReplay, pauseReplay };
};
